import React, { useEffect, useState } from 'react';
import { Alert, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InstallMobileIcon from '@mui/icons-material/GetApp';
import { isStandaloneDisplay } from '../../utils/bookingPwa';

const DISMISS_KEY = 'booking_pwa_install_dismissed';

function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios;
}

/**
 * "Add to Home Screen" — Android install prompt or iOS instructions.
 */
export default function BookingInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const showIosHint = isIosSafari() && !isStandaloneDisplay();

  useEffect(() => {
    if (isStandaloneDisplay()) return undefined;

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  if (isStandaloneDisplay() || dismissed) return null;

  if (showIosHint && !deferredPrompt) {
    return (
      <Alert
        severity="info"
        icon={<InstallMobileIcon />}
        sx={{ mb: 2 }}
        action={
          <IconButton size="small" aria-label="Dismiss" onClick={() => {
            setDismissed(true);
            try {
              sessionStorage.setItem(DISMISS_KEY, '1');
            } catch {
              /* ignore */
            }
          }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <strong>Add to Home Screen:</strong> tap the Share button in Safari, then choose &quot;Add to Home
        Screen&quot; for a full-screen booking app.
      </Alert>
    );
  }

  if (!deferredPrompt) return null;

  const handleInstall = async () => {
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <Alert
      severity="info"
      icon={<InstallMobileIcon />}
      sx={{ mb: 2, alignItems: 'flex-start' }}
      action={
        <IconButton size="small" aria-label="Dismiss" onClick={handleDismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      Install this booking app on your home screen for quicker access.
      <Button size="small" variant="contained" onClick={handleInstall} sx={{ display: 'block', mt: 1, fontWeight: 800 }}>
        Install app
      </Button>
    </Alert>
  );
}
